import React from "react";
import Pager from "../components/ui/Pager";
import { ArrowUp, ArrowDown } from "lucide-react";
import { tableWidthRem } from "./globalDataTabs";

/**
 * The table shared by Global Data and a practice's Tenant 360 drill-down: colgroup, sortable
 * header (`aria-sort`), rows, the empty row and the pager. The caller owns the data, the page
 * and the sort; this owns only layout.
 *
 * columns: [{ id, headerKey, width (rem), sort?, render(row, ctx) }]  (see globalDataTabs.jsx)
 * items:   [{ id, ...row }]   the rows of the CURRENT page
 * total:   int                every row the server holds for the query (drives the pager)
 * page:    int, 1-based       pageSize: int       onPage(page)
 * sort:    "key" | "-key"     the wire sort in force   onSort(columnSortKey)
 * ctx:     { t, lang, loc }   `t` is the superadmin translator
 * testIdPrefix: "gdv" | "t360s"   every test id is `${prefix}-table|sort-<col>|row-<id>|cell-<id>-<col>|pager`
 */
export default function GlobalDataTable({
  columns, items, total, page, pageSize, onPage, sort, onSort, ctx, testIdPrefix, emptyText,
}) {
  const { t } = ctx;
  const sortKey = sort.replace(/^-/, "");
  const sortDir = sort.startsWith("-") ? "desc" : "asc";
  const p = testIdPrefix;

  return (
    <>
      {/* Fixed layout: the column widths come from the column config, not from whatever the
          widest cell holds, so a 600-character unbroken string wraps inside its own cell
          instead of widening the table past its container. `minWidth` is the sum of the
          bases: below it the wrapper scrolls (a narrow viewport) rather than crushing the
          columns; above it the extra width is shared in proportion. */}
      <div className="overflow-x-auto">
        <table className="w-full table-fixed text-sm" style={{ minWidth: `${tableWidthRem({ columns })}rem` }} data-testid={`${p}-table`}>
          <colgroup>
            {columns.map((c) => <col key={c.id} style={{ width: `${c.width}rem` }} />)}
          </colgroup>
          <thead className="bg-slate-50 text-slate-400 text-xs uppercase tracking-wider">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.id}
                  scope="col"
                  aria-sort={c.sort && c.sort === sortKey ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
                  className="text-left px-3 py-3 align-bottom break-words"
                >
                  {c.sort ? (
                    <button
                      type="button"
                      onClick={() => onSort(c.sort)}
                      data-testid={`${p}-sort-${c.id}`}
                      data-direction={c.sort === sortKey ? sortDir : undefined}
                      className="inline-flex max-w-full items-center gap-1 text-left uppercase tracking-wider hover:text-slate-700"
                    >
                      {t(c.headerKey)}
                      {c.sort === sortKey && (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </button>
                  ) : t(c.headerKey)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-slate-100" data-testid={`${p}-row-${item.id}`}>
                {columns.map((c) => (
                  <td key={c.id} className="px-3 py-3 align-middle break-words" data-testid={`${p}-cell-${item.id}-${c.id}`}>
                    {c.render(item, ctx)}
                  </td>
                ))}
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-slate-400">{emptyText}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {total > pageSize && (
        <Pager
          page={page}
          pageSize={pageSize}
          total={total}
          shown={items.length}
          onPage={onPage}
          infoText={(v) => t("sal.pagerInfo", v)}
          prevLabel={t("sal.pagerPrev")}
          nextLabel={t("sal.pagerNext")}
          testId={`${p}-pager`}
          tone="slate"
        />
      )}
    </>
  );
}
